import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import Loading from "./Loading/Loading";
import Pdf from "./Pdf/Pdf";
import Error from "./Error/Error";
import Iframe from "./Iframe/Iframe";
import {
  onDocumentLoadSuccess,
  onDocumentLoadError,
  statusCodeMap,
  clientErrorStatusCodes,
  HTTP_STATUS_CODE,
  CUSTOM_STATUS_CODE,
  DEFAULT_PATH,
  CONTENT_TYPE,
  getBaseJapiUrl
} from "./helpers";
import styles from "./ReportViewer.module.scss";

export function ReportViewer() {
  const { pid, resourceType } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const [statusCode, setStatusCode] = useState(0);
  const [cdnUrl, setCdnUrl] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState(null);

  const baseJapiUrl = getBaseJapiUrl();
  const japiEndpoint = `${DEFAULT_PATH}/${pid}/${resourceType}`;
  const requestUrl = `${baseJapiUrl}${japiEndpoint}?token=${token}`;

  useEffect(() => {
    async function requestCdnUrl() {
      try {
        const res = await fetch(requestUrl);
        const resStatusCode = res.status;
        const contentType = res.headers.get("content-type");

        if (resStatusCode !== HTTP_STATUS_CODE.OK) {
          setError(statusCodeMap[resStatusCode]);
          return;
        }

        setStatusCode(resStatusCode);

        if (
          contentType &&
          (contentType.includes(CONTENT_TYPE.PDF) ||
            contentType.includes(CONTENT_TYPE.IMAGE_JPEG) ||
            contentType.includes(CONTENT_TYPE.IMAGE_PNG))
        ) {
          const arrayBuffer = await res.arrayBuffer();

          if (contentType.includes(CONTENT_TYPE.PDF)) {
            // Pass ArrayBuffer directly to react-pdf to avoid creating a blob URL,
            // which would be blocked by CSP connect-src on the extranet.
            setPdfFile({ data: arrayBuffer });
          } else {
            const blob = new Blob([arrayBuffer], { type: contentType });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `coverImage.${contentType.includes("jpeg") ? "jpg" : "png"}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        } else {
          const resText = await res.text();
          const ucid = new URL(resText).searchParams.get("ucid");

          if (ucid) sessionStorage.setItem("ext-ucid", ucid);

          setCdnUrl(resText);
        }
      } catch (errorObj) {
        setError(errorObj.message);
        setStatusCode(HTTP_STATUS_CODE.INTERNAL_SERVER_ERROR);
      }
    }

    requestCdnUrl().then(); // do nothing for now
  }, [japiEndpoint, token, baseJapiUrl, requestUrl]);

  function renderContent() {
    if (!cdnUrl && !pdfFile && statusCode === CUSTOM_STATUS_CODE.LOADING && !error) {
      return <Loading message={statusCodeMap[statusCode]} />;
    }

    if (error) return <Error error={error} />;

    if (pdfFile) {
      return (
        <Pdf
          pdfFile={pdfFile}
          numPages={numPages}
          onDocumentLoadSuccess={({ numPages: loadedPages }) => onDocumentLoadSuccess(loadedPages, setNumPages)}
          onDocumentLoadError={(docError) => onDocumentLoadError(docError, setStatusCode)}
        />
      );
    }

    return (
      <Iframe
        clientErrorStatusCodes={clientErrorStatusCodes}
        statusCode={statusCode}
        statusCodeMap={statusCodeMap}
        cdnUrl={cdnUrl}
      />
    );
  }

  return <div className={styles.reportViewer}>{renderContent()}</div>;
}
